import { ComponentRef, ReactElement, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorSummary, Field, Label, Paragraph, Textarea, ValidationMessage } from '@digdir/designsystemet-react';
import type { FieldError } from '../assessmentView';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

/** Takes focus when the errors change, so the user hears what to fix */
export const FormErrorSummary = ({ errors, title }: { errors: FieldError[]; title?: string }): ReactElement | null => {
  const { t } = useTranslation();
  const ref = useRef<ComponentRef<typeof ErrorSummary>>(null);

  useEffect(() => {
    if (errors.length > 0) ref.current?.focus();
  }, [errors]);

  if (errors.length === 0) return null;
  return (
    <ErrorSummary ref={ref}>
      <ErrorSummary.Heading>{title ?? t('workEnvironmentRisk.errorSummary')}</ErrorSummary.Heading>
      <ErrorSummary.List>
        {errors.map((error) => (
          <ErrorSummary.Item key={error.fieldId}>
            <ErrorSummary.Link href={`#${error.fieldId}`}>{error.message}</ErrorSummary.Link>
          </ErrorSummary.Item>
        ))}
      </ErrorSummary.List>
    </ErrorSummary>
  );
};

type TextFieldProps = {
  id: string;
  label: string;
  help?: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
};

export const TextField = ({ id, label, help, value, error, onChange }: TextFieldProps): ReactElement => (
  <Field data-size="sm">
    <Label htmlFor={id}>{label}</Label>
    {help && <Field.Description id={`${id}-help`}>{help}</Field.Description>}
    <Textarea
      id={id}
      rows={3}
      value={value}
      aria-describedby={help ? `${id}-help` : undefined}
      aria-invalid={Boolean(error) || undefined}
      onChange={(event) => onChange(event.target.value)}
    />
    {error && <ValidationMessage>{error}</ValidationMessage>}
  </Field>
);

/** A saved answer with its question as the label */
export const SavedText = ({ title, text }: { title: string; text: string }): ReactElement => (
  <div className={classes.savedText}>
    <span className="ds-label" data-size="sm">
      {title}
    </span>
    <Paragraph data-size="sm">{text}</Paragraph>
  </div>
);
